import React, { forwardRef } from "react";
import { FiHome, FiSearch } from "react-icons/fi";
import { FaChevronRight } from "react-icons/fa";
import classNames from "classnames";
import IStyledProps from "../../utilities/IStyledProps";
import { usePageProps } from '../../context/PagePropContext';
import styled from "styled-components";
import { SettingsEnum, getSettingAsObject } from "../../utilities/settings";
import useQueryParam from "../../utilities/useQueryParam";
import useLanguage from "../../utilities/useLanguage";
import ModalTrigger from "../Modals/ModalTrigger";
import ModalEnum from "../Modals/BaseModal/ModalEnum";
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../utilities/automation";
import SearchModal from "../../components/Modals/SearchModal";
import IEditableProps from "../../utilities/IEditableProps";

export interface IBlogSubNav extends IStyledProps, IEditableProps {}

const BlogSubnav = forwardRef((props: IBlogSubNav, ref: any) => {
    const { className } = props;
    const [tag] = useQueryParam('tag', '');
    const [query] = useQueryParam('q', '');
    const { lang } = useLanguage();
    const { blogCategory, pageType } = usePageProps(); // these props come in separate from 'props' because they need to be in frontmatter for meta tags
    const isBlogPage = pageType === 'blog';
    const allBlogCategories = getSettingAsObject(SettingsEnum.BLOG_CATEGORIES);
    const blogCategoriesLangV2 = (allBlogCategories[lang] ? allBlogCategories[lang] : (allBlogCategories['en'] || [])).filter(item => item.v2);
    return (
        <div ref={ref} data-id={props.cmsId} className={classNames("bg-carbon", {'is-blog-page': isBlogPage}, className)} role="navigation" aria-label="Blog Sub Navigation">
            <div className={classNames('container d-flex text-white text-uppercase justify-content-between align-items-center text-xs')}>
                {tag && !isBlogPage ? (
                    <span className="d-flex align-items-center">
                        <a href="/must-reads" className="p-2"><span className="d-flex align-items-center mr-3 home"><FiHome /></span></a>
                        <span className="d-flex align-items-center mr-3 chevron"><FaChevronRight /></span>
                        <span>TAG: {tag}</span>
                    </span>
                ) :
                query && !isBlogPage ? (
                    <span className="d-flex align-items-center">
                        <a href="/must-reads" className="p-2"><span className="d-flex align-items-center mr-3 home"><FiHome /></span></a>
                        <span className="d-flex align-items-center mr-3 chevron"><FaChevronRight /></span>
                        <span>SEARCH: {query}</span>
                    </span>
                ) : (
                    <>
                        <a href="/must-reads" className={classNames("px-2 text-white", blogCategoriesLangV2.find(item => item.key === blogCategory) ? 'text-decoration-none': 'text-decoration-underline')}>
                            All
                        </a>
                        {blogCategoriesLangV2.map(({key, value}) => {
                            return (
                                <a key={key} href={`/must-reads/${value}`} className={classNames(`px-2 text-white text-nowrap`, blogCategory === value ? 'text-decoration-underline': 'text-decoration-none')}>
                                    {key}
                                </a>
                            );
                        })}
                        <ModalTrigger modal={ModalEnum.BlogSearch} >
                            <a data-test-id={createDataTestId({placement: AutomationPlacement.BLOG, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.LINK, specifier: 'subnav'})} className="px-2 modal-search-button d-inline-flex align-items-center">
                                <span className="search-icon"><FiSearch /></span>
                            </a>
                        </ModalTrigger>
                    </>
                )}
                <SearchModal isBlogSearch />
            </div>
        </div>
    );
});

export default styled(BlogSubnav)`
    height: 2.5rem;
    background: var(--carbon);
    white-space: nowrap;
    font-size: 10px;
    font-weight: 500;
    overflow-x: auto;
    /* Uncomment to hide scrollbars in all browsers */
    /* scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE 10+
    &::-webkit-scrollbar { // WebKit
        width: 0;
        height: 0;
    } */
    // hide subnav
    display: none;

    &:not(.is-blog-page) {
        // show subnav on non-blog pages
        display: flex;
        overflow-x: auto;
    }
    &.is-blog-page {
        // show subnav on blog pages above 768 breakpoint (breadcrumbs show below 768 breakpoint)
        @media (min-width: 768px) {
            display: flex;
            overflow-x: auto;
            white-space: wrap;
        }
    }
    .container {
        /* min-width: 768px; */
    }

    .chevron {
        color: #fff;
        svg {
            height: 13.5px;
            width: 13.5px;
        }
    }
    .home svg {
        height: 13.5px;
        width: 13.5px;
        stroke: #fff;
    }
    .search-icon svg {
        height: 24px;
        width: 24px;
        stroke: #fff;
    }
`;
